import React from 'react'
import { useSelector } from 'hooks/use-redux'
import { Link } from 'react-router-dom'
import ContactUsLink from 'components/ui/contact-us-link'
import { getIsTrial } from '@disco/state-management/selectors/business'
import { isDisableInviteTeamMember } from '@disco/state-management/selectors/invites'
import { getUsersLimits } from '@disco/state-management/selectors/user'
import { getSelfServeBillingPermissions } from '@disco/state-management/selectors/subscription'
import Alert from 'components/ui/alert'
import type { IconKey } from 'components/ui/icon'

/**
 * UsersQuotaAlert component for displaying user quota warnings
 *
 * A functional component that monitors user seat usage and displays appropriate
 * alerts when approaching or exceeding quota limits. Provides contextual styling
 * and links to upgrade seats when needed. Excludes trial users and disabled
 * invite scenarios.
 *
 * @returns JSX element containing quota alert or null if not needed
 */
const UsersQuotaAlert = (): null | React.ReactNode => {
  const usersLimits = useSelector(getUsersLimits)
  const disableInviteTeamMember = useSelector(isDisableInviteTeamMember)
  // trials should have no limits on inviting users.
  const isTrial = useSelector(getIsTrial)
  const billingPermissons = useSelector(getSelfServeBillingPermissions)
  let status: React.ComponentProps<typeof Alert>['status'] = 'info'
  let icon: IconKey = 'info'

  if (usersLimits.overQuota) {
    status = 'danger'
    icon = 'warning'
  }

  if (usersLimits.current === usersLimits.max) {
    status = 'warning'
    icon = 'warning'
  }

  if (isTrial || disableInviteTeamMember) return null
  return (
    <Alert status={status} icon={icon}>
      You’re using {usersLimits.current} of your{' '}
      {usersLimits.maxAsDisplayString}{' '}
      <span>
        <span>{usersLimits.max === 1 ? 'seat' : 'seats'}</span>
        <span>. </span>
      </span>
      {billingPermissons.no_seats_and_tracks_due_to ? (
        <ContactUsLink className="text-link">Add more seats.</ContactUsLink>
      ) : (
        <Link
          className="text-link"
          to="/settings/billing/update-seats-and-tracks"
        >
          Add more seats.
        </Link>
      )}
    </Alert>
  )
}

export default UsersQuotaAlert
